import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import MainLayout from "@/components/MainLayout";
import LoggedInWelcomeModal from "@/components/LoggedInWelcomeModal";
import HomePage from "@/pages/HomePage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import EventsPage from "@/pages/EventsPage";
import WorkWithUsPage from "@/pages/WorkWithUsPage";
import JoinTeamPage from "@/pages/JoinTeamPage";
import AmbassadorPage from "@/pages/AmbassadorPage";
import SponsorPage from "@/pages/SponsorPage";
import MerchPage from "@/pages/MerchPage";
import PrivacyPolicyPage from "@/pages/PrivacyPolicyPage";
import TermsPage from "@/pages/TermsPage";
import AccessibilityStatementPage from "@/pages/AccessibilityStatementPage";
import DirectoryPage from "@/pages/DirectoryPage";
import MarketDirectoryPage from "@/pages/MarketDirectoryPage";
import LoginPage from "@/pages/LoginPage";
import RegisterPage from "@/pages/RegisterPage";
import ShopperRegisterPage from "@/pages/ShopperRegisterPage";
import DashboardPage from "@/pages/DashboardPage";
import MarketDashboardPage from "@/pages/MarketDashboardPage";
import ShopperProfilePage from "@/pages/ShopperProfilePage";
import SubscribePage from "@/pages/SubscribePage";
import SubscribeSuccessPage from "@/pages/SubscribeSuccessPage";
import AdminLoginPage from "@/pages/AdminLoginPage";
import AdminDashboardPage from "@/pages/AdminDashboardPage";
import AdminBusinessEditPage from "@/pages/AdminBusinessEditPage";
import AdminMarketEditPage from "@/pages/AdminMarketEditPage";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";

const accountHome = (userType) => (
  userType === "market" ? "/market-dashboard" : userType === "shopper" ? "/shopper-profile" : "/dashboard"
);

const ProtectedRoute = ({ children, allowedTypes }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="page-shell flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (allowedTypes && !allowedTypes.includes(user?.userType || "business")) {
    return <Navigate to={accountHome(user?.userType)} replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, user } = useAuth();
  if (loading) {
    return (
      <div className="page-shell flex min-h-screen items-center justify-center">
        <div className="text-muted-foreground">Loading...</div>
      </div>
    );
  }
  if (isAuthenticated) {
    return <Navigate to={accountHome(user?.userType)} replace />;
  }
  return children;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public pages with shared layout */}
          <Route path="/" element={<MainLayout><HomePage /></MainLayout>} />
          <Route path="/about" element={<MainLayout><AboutPage /></MainLayout>} />
          <Route path="/contact" element={<MainLayout><ContactPage /></MainLayout>} />
          <Route path="/events" element={<MainLayout><EventsPage /></MainLayout>} />
          <Route path="/work-with-us" element={<MainLayout><WorkWithUsPage /></MainLayout>} />
          <Route path="/join-the-team" element={<MainLayout><JoinTeamPage /></MainLayout>} />
          <Route path="/ambassador" element={<MainLayout><AmbassadorPage /></MainLayout>} />
          <Route path="/sponsor" element={<MainLayout><SponsorPage /></MainLayout>} />
          <Route path="/merch" element={<MainLayout><MerchPage /></MainLayout>} />
          <Route path="/privacy-policy" element={<MainLayout><PrivacyPolicyPage /></MainLayout>} />
          <Route path="/terms-and-conditions" element={<MainLayout><TermsPage /></MainLayout>} />
          <Route path="/accessibility" element={<MainLayout><AccessibilityStatementPage /></MainLayout>} />
          <Route path="/directory" element={<MainLayout><DirectoryPage /></MainLayout>} />
          <Route path="/markets" element={<MainLayout><MarketDirectoryPage /></MainLayout>} />
          <Route path="/register" element={<MainLayout><RegisterPage /></MainLayout>} />
          <Route path="/shopper-signup" element={<MainLayout><ShopperRegisterPage /></MainLayout>} />

          {/* Auth pages */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <MainLayout><LoginPage /></MainLayout>
              </PublicRoute>
            }
          />

          {/* Protected pages */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute allowedTypes={["business"]}>
                <DashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/market-dashboard"
            element={
              <ProtectedRoute allowedTypes={["market"]}>
                <MarketDashboardPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/shopper-profile"
            element={
              <ProtectedRoute allowedTypes={["shopper"]}>
                <ShopperProfilePage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscribe"
            element={
              <ProtectedRoute>
                <MainLayout><SubscribePage /></MainLayout>
              </ProtectedRoute>
            }
          />
          <Route
            path="/subscribe/success"
            element={
              <ProtectedRoute>
                <MainLayout><SubscribeSuccessPage /></MainLayout>
              </ProtectedRoute>
            }
          />

          {/* Admin Routes (no shared layout — separate portal) */}
          <Route path="/admin" element={<AdminLoginPage />} />
          <Route path="/admin/dashboard" element={<AdminDashboardPage />} />
          <Route path="/admin/business/:email" element={<AdminBusinessEditPage />} />
          <Route path="/admin/market/:marketId" element={<AdminMarketEditPage />} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <LoggedInWelcomeModal />
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
